import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { bannerTemplates, getBannerImageUrl } from "@/mocks/bannerTemplates";
import { logActivity } from "@/lib/activityLog";
import useMember from "@/hooks/useMember";

const categoryOptions = [
  {
    value: "divulgar-negocio",
    label: "Divulgar meu negócio",
    icon: "ri-store-2-line",
    description: "Tenho um produto ou serviço e quero divulgar para a comunidade",
  },
  {
    value: "oferecer-vaga",
    label: "Oferecer vaga de trabalho",
    icon: "ri-briefcase-line",
    description: "Estou contratando ou sei de uma oportunidade para a comunidade",
  },
  {
    value: "procurar-vaga",
    label: "Procurar vaga de trabalho",
    icon: "ri-search-line",
    description: "Estou em busca de uma oportunidade profissional",
  },
  {
    value: "pedir-indicacao",
    label: "Pedir indicação ou referência",
    icon: "ri-user-heart-line",
    description: "Preciso de uma profissional de confiança indicada pela comunidade",
  },
  {
    value: "outro",
    label: "Outro tipo de anúncio",
    icon: "ri-megaphone-line",
    description: "Parceria, evento, colaboração ou outro tipo de divulgação",
  },
];

const CATEGORY_BUTTON_TEXT: Record<string, string> = {
  "divulgar-negocio": "Quero conhecer",
  "oferecer-vaga": "Quero me candidatar",
  "procurar-vaga": "Quero indicar",
  "pedir-indicacao": "Quero indicar",
  "outro": "Saiba mais",
};

const TITLE_PLACEHOLDERS: Record<string, string> = {
  "divulgar-negocio": 'Ex: "Consultoria de imagem para mulheres empreendedoras"',
  "oferecer-vaga": 'Ex: "Vaga: Assistente Virtual & Gestora de Redes Sociais"',
  "procurar-vaga": 'Ex: "Busco vaga de Assistente Administrativo — Home Office"',
  "pedir-indicacao": 'Ex: "Preciso de indicação de profissional de estética de confiança"',
  "outro": 'Ex: "Evento de networking para mulheres empreendedoras"',
};

const INITIAL_FORM = {
  category: "",
  title: "",
  description: "",
  button_text: "",
  contact_name: "",
  contact_whatsapp: "",
  contact_email: "",
  link: "",
  phone_country: "BR",
};

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_ACTIVE_BANNERS = 3;

interface ActiveBanner {
  id: number;
  title: string;
  image_url: string;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result?.toString().split(",")[1] || "";
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function generateApprovalToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

function buildSiteBaseUrl(): string {
  const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
  const pathPrefix = basePath ? `/${basePath}` : "";
  return `${window.location.origin}${pathPrefix}`;
}

export default function SubmitBannerForm() {
  const { member } = useMember();

  const [form, setForm] = useState({ ...INITIAL_FORM });
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [customImageUrl, setCustomImageUrl] = useState("");
  const [customImagePreview, setCustomImagePreview] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Limite de 3 anúncios ativos
  const [showLimit, setShowLimit] = useState(false);
  const [activeBanners, setActiveBanners] = useState<ActiveBanner[]>([]);
  const [deactivatingId, setDeactivatingId] = useState<number | null>(null);

  const customImageInputRef = useRef<HTMLInputElement>(null);

  function formatPhone(value: string, country: string): string {
    const digits = value.replace(/\D/g, "");
    if (country === "BR") {
      if (digits.length <= 2) return `(${digits}`;
      if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
      if (digits.length <= 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
    }
    if (country === "US") {
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
    return value;
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    if (name === "contact_whatsapp") {
      setForm({ ...form, [name]: formatPhone(value, form.phone_country) });
    } else {
      setForm({ ...form, [name]: value });
    }
    setErrorMessage("");
  };

  const handleCategorySelect = (value: string) => {
    setForm({ ...form, category: value });
    setErrorMessage("");
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setCustomImageUrl("");
    setCustomImagePreview("");
    setImageError("");
    setErrorMessage("");
  };

  const handleCountryChange = (country: string) => {
    setForm({ ...form, phone_country: country, contact_whatsapp: "" });
    setErrorMessage("");
  };

  const handleCustomImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setImageError("Formato inválido. Use apenas JPG, PNG ou WebP.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setImageError("Imagem muito grande. O limite é 5MB.");
      e.target.value = "";
      return;
    }

    setImageError("");
    setErrorMessage("");
    setCustomImagePreview(URL.createObjectURL(file));
    setSelectedTemplateId("");
    setUploadingImage(true);

    try {
      const base64 = await fileToBase64(file);
      const { data, error: fnError } = await supabase.functions.invoke("upload-banner-image", {
        body: {
          filename: file.name,
          mimeType: file.type,
          base64Data: base64,
        },
      });

      if (fnError || !data || !data.success) {
        setImageError(data?.error || "Erro ao enviar a imagem. Tente novamente.");
        setCustomImagePreview("");
        setCustomImageUrl("");
      } else {
        setCustomImageUrl(data.url);
      }
    } catch {
      setImageError("Erro ao enviar a imagem. Tente novamente.");
      setCustomImagePreview("");
      setCustomImageUrl("");
    } finally {
      setUploadingImage(false);
    }
  };

  async function loadActiveBanners(): Promise<ActiveBanner[]> {
    if (!member?.id) return [];
    const { data } = await supabase
      .from("banners")
      .select("id,title,image_url")
      .eq("member_id", member.id)
      .eq("active", true);
    return (data as ActiveBanner[]) || [];
  }

  async function deactivateBanner(id: number) {
    setDeactivatingId(id);
    const { error } = await supabase.from("banners").update({ active: false }).eq("id", id);
    setDeactivatingId(null);

    if (error) {
      setErrorMessage("Erro ao desativar o anúncio. Tente novamente.");
      return;
    }

    const remaining = await loadActiveBanners();
    setActiveBanners(remaining);
    if (remaining.length < MAX_ACTIVE_BANNERS) {
      setShowLimit(false);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");

    if (!form.category) {
      setErrorMessage("Selecione o tipo de anúncio.");
      return;
    }
    if (!selectedTemplateId && !customImageUrl) {
      setErrorMessage("Escolha um template ou envie a sua própria imagem.");
      return;
    }
    if (!form.title.trim()) {
      setErrorMessage("O nome do anúncio é obrigatório.");
      return;
    }
    if (selectedTemplateId && !form.description.trim()) {
      setErrorMessage("A descrição do anúncio é obrigatória.");
      return;
    }
    if (!form.contact_name.trim()) {
      setErrorMessage("Seu nome é obrigatório.");
      return;
    }
    if (!form.contact_email.trim()) {
      setErrorMessage("Seu email é obrigatório.");
      return;
    }
    if (!member?.id) {
      setErrorMessage("Não foi possível identificar sua conta. Recarregue a página e tente novamente.");
      return;
    }

    setSubmitting(true);

    // Verifica limite de 3 anúncios ativos (pendentes não contam)
    const active = await loadActiveBanners();
    if (active.length >= MAX_ACTIVE_BANNERS) {
      setActiveBanners(active);
      setShowLimit(true);
      setSubmitting(false);
      return;
    }

    const selectedTemplate = bannerTemplates.find((t) => t.id === selectedTemplateId) || null;

    const categoryLabel =
      categoryOptions.find((c) => c.value === form.category)?.label || form.category;

    const bannerTitle = customImageUrl
      ? form.title.trim()
      : `[${categoryLabel}] ${form.title.trim()}`;
    const bannerImageUrl =
      customImageUrl || (selectedTemplate ? getBannerImageUrl(selectedTemplate) : "");
    const approvalToken = generateApprovalToken();
    const baseUrl = buildSiteBaseUrl();

    const payload = {
      category: selectedTemplate ? selectedTemplate.category : null,
      template_id: selectedTemplate ? selectedTemplate.id : null,
      title: bannerTitle,
      subtitle: form.description.trim() || null,
      button_text: selectedTemplate
        ? CATEGORY_BUTTON_TEXT[form.category] || "Saiba mais"
        : form.button_text.trim() || null,
      button_link: form.link.trim() || "/enviar-anuncio",
      image_url: bannerImageUrl,
      sort_order: 999,
      active: false,
      member_id: member.id,
      moderation_status: "pending",
      approval_token: approvalToken,
      contact_info: {
        name: form.contact_name.trim(),
        whatsapp: form.contact_whatsapp.trim() || null,
        email: form.contact_email.trim(),
      },
    };

    const { data: inserted, error } = await supabase
      .from("banners")
      .insert(payload)
      .select("id")
      .single();

    if (error || !inserted) {
      setErrorMessage("Erro ao enviar seu anúncio. Tente novamente.");
      setSubmitting(false);
      return;
    }

    const bannerId = inserted.id;

    // Fire-and-forget: email de moderação para a admin (com botões aprovar/recusar)
    supabase.functions.invoke("notify-banner-moderation", {
      body: {
        baseUrl,
        bannerId,
        title: bannerTitle,
        description: form.description.trim(),
        category: categoryLabel,
        buttonLink: form.link.trim() || "/enviar-anuncio",
        imageUrl: bannerImageUrl,
        memberName: member.full_name || form.contact_name.trim(),
        memberId: member.id,
        contactName: form.contact_name.trim(),
        contactWhatsapp: form.contact_whatsapp.trim() || "",
        contactEmail: form.contact_email.trim(),
        approvalToken,
      },
    }).then(({ error: notifyError }) => {
      if (notifyError) {
        console.error("Falha ao notificar admin:", notifyError);
      }
    });

    // Fire-and-forget: confirmação "recebido, em análise" para a própria membro
    supabase.functions.invoke("notify-banner-published", {
      body: {
        memberName: member.full_name || form.contact_name.trim(),
        memberEmail: member.email || form.contact_email.trim(),
        bannerTitle,
      },
    }).then(({ error: publishError }) => {
      if (publishError) {
        console.error("Falha ao enviar confirmação à membro:", publishError);
      }
    });

    logActivity("banner_submit", {
      category: form.category,
      title: form.title.trim(),
      contact_name: form.contact_name.trim(),
      member_id: member.id,
    });

    setSubmitted(true);
    setSubmitting(false);
  };

  if (showLimit) {
    return (
      <div className="w-full px-6 md:px-10 lg:px-16 max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl p-10 md:p-14">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-amber-50 mx-auto mb-6">
            <i className="ri-notification-3-line text-2xl text-amber-500" />
          </div>
          <h3 className="font-display text-2xl text-brown mb-3">
            Você já tem 3 anúncios no ar
          </h3>
          <p className="font-body text-brown/70 text-base leading-relaxed mb-8">
            Para publicar um novo anúncio, desative um dos seus anúncios ativos
            abaixo para liberar espaço.
          </p>

          {errorMessage && (
            <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 font-body text-sm flex items-start gap-2 mb-6">
              <i className="ri-error-warning-line mt-0.5 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div className="space-y-3 mb-8">
            {activeBanners.map((banner) => (
              <div
                key={banner.id}
                className="flex items-center gap-4 p-4 rounded-xl border border-nude bg-beige/40"
              >
                <div className="w-24 h-14 rounded-lg overflow-hidden shrink-0 bg-beige">
                  <img
                    src={banner.image_url}
                    alt={banner.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-body text-sm text-brown leading-snug line-clamp-2">
                    {banner.title}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => deactivateBanner(banner.id)}
                  disabled={deactivatingId === banner.id}
                  className="px-4 py-2 rounded-full bg-white border border-nude text-brown font-body text-xs hover:bg-nude/40 transition-colors whitespace-nowrap shrink-0 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {deactivatingId === banner.id ? "Desativando..." : "Desativar"}
                </button>
              </div>
            ))}
          </div>

          <div className="text-center">
            <button
              onClick={() => setShowLimit(false)}
              className="font-body text-sm px-8 py-3 rounded-full bg-primary text-white tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark whitespace-nowrap cursor-pointer"
            >
              Voltar para o formulário
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="w-full px-6 md:px-10 lg:px-16 max-w-2xl mx-auto text-center">
        <div className="bg-white rounded-2xl p-10 md:p-14">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary/10 mx-auto mb-6">
            <i className="ri-check-line text-2xl text-primary" />
          </div>
          <h3 className="font-display text-2xl text-brown mb-3">
            Anúncio enviado para aprovação!
          </h3>
          <p className="font-body text-brown/70 text-base leading-relaxed mb-2">
            Recebemos seu anúncio e ele já está <strong>em análise</strong>.
          </p>
          <p className="font-body text-brown/60 text-sm leading-relaxed mb-8">
            Assim que a administradora aprovar, ele entrará no ar no carrossel
            da página inicial e você receberá um email de confirmação.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/meus-anuncios"
              className="font-body text-sm px-8 py-3 rounded-full bg-primary text-white tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark whitespace-nowrap"
            >
              Ver meus anúncios
            </Link>
            <button
              onClick={() => {
                setSubmitted(false);
                setForm({ ...INITIAL_FORM });
                setSelectedTemplateId("");
                setCustomImageUrl("");
                setCustomImagePreview("");
              }}
              className="font-body text-sm px-8 py-3 rounded-full bg-beige border border-nude text-brown tracking-wide uppercase transition-all duration-300 hover:bg-nude/50 whitespace-nowrap cursor-pointer"
            >
              Enviar outro anúncio
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-6 md:px-10 lg:px-16 max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center mb-10 md:mb-14">
        <p className="font-body text-xs tracking-[0.25em] uppercase text-primary mb-3">
          Anuncie na Comunidade
        </p>
        <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-brown mb-4">
          Divulgue seu anúncio
        </h2>
        <p className="font-body text-brown/70 text-base leading-relaxed max-w-xl mx-auto">
          Divulgue seu negócio, ofereça ou procure vagas, peça indicações.
          Seu anúncio passa pela aprovação da administradora e, ao ser aprovado,
          aparece no carrossel da página inicial para toda a rede.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 md:p-10 space-y-6" noValidate>
        {/* Error message */}
        {errorMessage && (
          <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 font-body text-sm flex items-start gap-2">
            <i className="ri-error-warning-line mt-0.5 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Step 1: Tipo de anúncio */}
        <div>
          <label className="font-display text-base text-brown block mb-4">
            1. Qual o tipo do seu anúncio? <span className="text-primary">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {categoryOptions.map((cat) => (
              <button
                key={cat.value}
                type="button"
                onClick={() => handleCategorySelect(cat.value)}
                className={`text-left p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                  form.category === cat.value
                    ? "border-primary bg-primary/5"
                    : "border-nude bg-beige/30 hover:border-brown-light/30"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-9 h-9 flex items-center justify-center rounded-full shrink-0 ${
                      form.category === cat.value
                        ? "bg-primary text-white"
                        : "bg-beige text-brown-light"
                    }`}
                  >
                    <i className={`${cat.icon} text-base`} />
                  </div>
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <p className="font-body text-sm text-brown break-words leading-snug">
                      {cat.label}
                    </p>
                    <p className="font-body text-xs text-brown/50 mt-1 leading-relaxed break-words">
                      {cat.description}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <hr className="border-nude" />

        {/* Step 2: Escolha o template */}
        <div>
          <label className="font-display text-base text-brown block mb-2">
            2. Escolha o visual do seu banner <span className="text-primary">*</span>
          </label>
          <p className="font-body text-sm text-brown/50 mb-5 leading-relaxed">
            Escolha um dos templates prontos ou envie a sua própria arte
            (JPG, PNG ou WebP, até 5MB).
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Upload da própria imagem */}
            <button
              type="button"
              onClick={() => customImageInputRef.current?.click()}
              className={`group relative rounded-xl overflow-hidden border-2 transition-all duration-200 cursor-pointer text-left ${
                customImageUrl
                  ? "border-primary ring-2 ring-primary/30"
                  : "border-dashed border-nude hover:border-primary/40"
              }`}
            >
              <div className="aspect-[1440/400] w-full relative bg-beige/50">
                {customImagePreview ? (
                  <img
                    src={customImagePreview}
                    alt="Sua imagem"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-3">
                    {uploadingImage ? (
                      <>
                        <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                        <span className="font-body text-xs text-brown/60">
                          Enviando imagem...
                        </span>
                      </>
                    ) : (
                      <>
                        <i className="ri-upload-cloud-2-line text-2xl text-brown/40" />
                        <span className="font-body text-xs text-brown/60 text-center leading-snug">
                          Usar minha própria imagem
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
              {customImageUrl && !uploadingImage && (
                <div className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-primary text-white">
                  <i className="ri-check-line text-xs" />
                </div>
              )}
            </button>
            <input
              ref={customImageInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
              onChange={handleCustomImage}
              className="hidden"
            />

            {bannerTemplates.map((tpl) => {
              const isSelected = selectedTemplateId === tpl.id;
              const isPalette = !!tpl.palette;
              return (
                <button
                  key={tpl.id}
                  type="button"
                  onClick={() => handleTemplateSelect(tpl.id)}
                  className={`group relative rounded-xl overflow-hidden border-2 transition-all duration-200 cursor-pointer text-left ${
                    isSelected
                      ? "border-primary ring-2 ring-primary/30"
                      : "border-nude hover:border-primary/40"
                  }`}
                >
                  {isPalette && tpl.palette ? (
                    <>
                      {/* Gradient background */}
                      <div className="aspect-[1440/400] w-full overflow-hidden">
                        <div
                          className="w-full h-full transition-transform duration-300 group-hover:scale-[1.03]"
                          style={{
                            background: `linear-gradient(135deg, ${tpl.palette.primary} 0%, ${tpl.palette.accent} 50%, ${tpl.palette.background} 100%)`,
                          }}
                        />
                      </div>
                      {/* Color swatches */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent p-3 flex items-center gap-1.5">
                        <span
                          className="w-3 h-3 rounded-full border border-white/50"
                          style={{ backgroundColor: tpl.palette.primary }}
                        />
                        <span
                          className="w-3 h-3 rounded-full border border-white/50"
                          style={{ backgroundColor: tpl.palette.accent }}
                        />
                        <span
                          className="w-3 h-3 rounded-full border border-white/50"
                          style={{ backgroundColor: tpl.palette.background }}
                        />
                        <span className="font-body text-xs text-white font-medium tracking-wide ml-1.5">
                          {tpl.category}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="aspect-[1440/400] w-full overflow-hidden">
                        <img
                          src={tpl.image_url}
                          alt={tpl.label}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                          loading="lazy"
                        />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-3">
                        <span className="font-body text-xs text-white font-medium tracking-wide">
                          {tpl.category}
                        </span>
                      </div>
                    </>
                  )}
                  {isSelected && (
                    <div className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-primary text-white">
                      <i className="ri-check-line text-xs" />
                    </div>
                  )}
                  <div className={`absolute inset-0 transition-colors duration-200 pointer-events-none ${
                    isSelected ? "bg-primary/5" : "bg-transparent group-hover:bg-black/5"
                  }`} />
                </button>
              );
            })}
          </div>
          {imageError && (
            <p className="font-body text-xs text-red-600 mt-3 flex items-center gap-1.5">
              <i className="ri-error-warning-line" />
              {imageError}
            </p>
          )}
          {!selectedTemplateId && !customImageUrl && !imageError && (
            <p className="font-body text-xs text-brown/40 mt-3 flex items-center gap-1.5">
              <i className="ri-information-line" />
              Selecione um dos templates acima ou envie sua própria imagem.
            </p>
          )}
        </div>

        <hr className="border-nude" />

        {/* Step 3: Conteúdo do anúncio */}
        <div>
          <label className="font-display text-base text-brown block mb-4">
            3. Conteúdo do anúncio
          </label>

          {customImageUrl && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 mb-4">
              <i className="ri-information-line text-amber-600 mt-0.5 shrink-0" />
              <p className="font-body text-xs text-amber-800 leading-relaxed">
                Sua arte já tem os textos? Deixe a descrição e o texto do botão em branco
                para não cobrir a imagem. O nome do anúncio é usado apenas internamente.
              </p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="font-body text-sm text-brown block mb-2">
                {customImageUrl ? (
                  <>
                    Nome do anúncio{" "}
                    <span className="text-brown/40 font-normal">
                      (uso interno, não aparece no site)
                    </span>{" "}
                    <span className="text-primary">*</span>
                  </>
                ) : (
                  <>
                    Título do anúncio <span className="text-primary">*</span>
                  </>
                )}
              </label>
              <input
                type="text"
                name="title"
                value={form.title}
                onChange={handleChange}
                maxLength={80}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                placeholder={
                  customImageUrl
                    ? 'Ex: "Consultoria de imagem — Novembro"'
                    : TITLE_PLACEHOLDERS[form.category] || 'Ex: "Consultoria de imagem para mulheres empreendedoras"'
                }
              />
              <p className="font-body text-xs text-brown/40 mt-1 text-right">
                {form.title.length}/80
              </p>
            </div>

            <div>
              <label className="font-body text-sm text-brown block mb-2">
                Descrição{" "}
                {customImageUrl ? (
                  <span className="text-brown/40 font-normal">(opcional)</span>
                ) : (
                  <span className="text-primary">*</span>
                )}
              </label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={4}
                maxLength={300}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors resize-none"
                placeholder="Descreva seu negócio, vaga ou o que você está procurando. Seja clara e direta!"
              />
              <p className="font-body text-xs text-brown/40 mt-1 text-right">
                {form.description.length}/300
              </p>
            </div>

            {customImageUrl && (
              <div>
                <label className="font-body text-sm text-brown block mb-2">
                  Texto do botão{" "}
                  <span className="text-brown/40 font-normal">(opcional)</span>
                </label>
                <input
                  type="text"
                  name="button_text"
                  value={form.button_text}
                  onChange={handleChange}
                  maxLength={40}
                  className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                  placeholder="Ex: Quero conhecer"
                />
                <p className="font-body text-xs text-brown/40 mt-1 text-right">
                  {form.button_text.length}/40
                </p>
              </div>
            )}
          </div>
        </div>

        <hr className="border-nude" />

        {/* Step 4: Contato */}
        <div>
          <label className="font-display text-base text-brown block mb-4">
            4. Seus dados de contato
          </label>
          <p className="font-body text-xs text-brown/40 mb-4">
            Essas informações ficarão visíveis apenas para a administradora. Elas
            não aparecerão no banner público.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-body text-sm text-brown block mb-2">
                Seu nome <span className="text-primary">*</span>
              </label>
              <input
                type="text"
                name="contact_name"
                value={form.contact_name}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                placeholder="Seu nome completo"
              />
            </div>
            <div>
              <label className="font-body text-sm text-brown block mb-2">
                WhatsApp{" "}
                <span className="text-brown/40 font-normal">(opcional)</span>
              </label>
              <div className="flex gap-2">
                <select
                  name="phone_country"
                  value={form.phone_country}
                  onChange={handleChange}
                  className="w-24 px-3 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors cursor-pointer shrink-0"
                >
                  <option value="BR">🇧🇷 BR</option>
                  <option value="US">🇺🇸 EUA</option>
                </select>
                <input
                  type="tel"
                  name="contact_whatsapp"
                  value={form.contact_whatsapp}
                  onChange={handleChange}
                  className="flex-1 px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                  placeholder={form.phone_country === "BR" ? "(11) 99999-9999" : "(561) 939-9706"}
                  maxLength={form.phone_country === "BR" ? 15 : 14}
                />
              </div>
              <p className="font-body text-[11px] text-brown/40 mt-1">
                {form.phone_country === "BR"
                  ? "Formato brasileiro: (DDD) 9XXXX-XXXX"
                  : "Formato americano: (Área) XXX-XXXX"}
              </p>
            </div>
            <div className="sm:col-span-2">
              <label className="font-body text-sm text-brown block mb-2">
                Email <span className="text-primary">*</span>
              </label>
              <input
                type="email"
                name="contact_email"
                value={form.contact_email}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                placeholder="seu@email.com"
              />
            </div>
          </div>
        </div>

        <hr className="border-nude" />

        {/* Step 5: Link */}
        <div>
          <label className="font-display text-base text-brown block mb-4">
            5. Link{" "}
            <span className="text-brown/40 font-normal text-sm">(opcional)</span>
          </label>
          <p className="font-body text-xs text-brown/40 mb-3">
            Um link para seu site, Instagram, LinkedIn ou página do seu negócio.
          </p>
          <input
            type="url"
            name="link"
            value={form.link}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
            placeholder="https://instagram.com/seunegocio"
          />
        </div>

        {/* Submit */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={submitting || uploadingImage}
            className="w-full font-body text-sm px-10 py-4 rounded-full bg-primary text-white tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin" />
                Enviando anúncio...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-send-plane-line" />
                Enviar anúncio para aprovação
              </span>
            )}
          </button>
          <p className="font-body text-xs text-brown/40 text-center mt-3">
            Seu anúncio será revisado pela administradora antes de ir ao ar.
          </p>
        </div>
      </form>
    </div>
  );
}